import { Link } from "react-router-dom";
import { gossipLiveUpdates } from "@/mocks/gossipLiveUpdates";
import { gossipTopics } from "@/mocks/gossipTopics";
import { gossipArticles } from "@/mocks/gossipArticles";
import { getCategoryName } from "@/utils/category";
import { formatHeat } from "@/utils/format";
import SectionHeading from "@/components/feature/SectionHeading";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  new: "ri-flashlight-fill",
};

const trendClass: Record<string, string> = {
  up: "text-primary-600",
  down: "text-accent-600",
  new: "text-secondary-600",
};

export default function LiveTimeline() {
  const recommended = [...gossipArticles].sort((a, b) => b.hotScore - a.hotScore).slice(0, 4);

  return (
    <section id="live" className="mx-auto max-w-shell scroll-mt-28 px-4 py-14 md:px-6 md:py-16">
      <SectionHeading
        eyebrow="Live Feed"
        title="今日更新 · 实时吃瓜流水线"
        description="按时间倒序记录每一条进展，重要节点会重点标记。有新的公开信息就继续往下补，不会断档。"
      />

      <div className="mt-10 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <ol className="relative border-l border-background-300 pl-6 md:pl-8">
          {gossipLiveUpdates.map((item, index) => (
            <li key={`${item.time}-${item.title}-${index}`} className="relative pb-8 last:pb-0">
              <span
                className={`absolute -left-[31px] top-1 flex h-3 w-3 items-center justify-center rounded-full ring-4 ring-background-50 md:-left-[39px] ${
                  item.important ? "bg-primary-500" : "bg-background-400"
                }`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-foreground-950 px-2.5 py-1 font-label text-xs font-bold text-background-50">
                  {item.time}
                </span>
                <Link
                  to={`/category/${item.categorySlug}`}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-label text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {getCategoryName(item.categorySlug)}
                </Link>
                {item.important && (
                  <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-label text-primary-700">
                    <i className="ri-fire-line" /> 重点
                  </span>
                )}
              </div>
              <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.detail}</p>
            </li>
          ))}
        </ol>

        <aside className="space-y-8">
          <div id="hot" className="scroll-mt-28 overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-fill text-primary-500" />
                热搜榜
              </h3>
              <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                <span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
                实时更新
              </span>
            </div>
            <ol className="divide-y divide-background-200">
              {gossipTopics.slice(0, 8).map((topic) => (
                <li key={topic.rank}>
                  <Link
                    to={`/category/${topic.categorySlug}`}
                    className="flex items-start gap-3 px-5 py-3.5 transition-colors hover:bg-background-50"
                  >
                    <span
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-label text-xs font-bold ${
                        topic.rank <= 3
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-300 text-foreground-700"
                      }`}
                    >
                      {topic.rank}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm leading-snug text-foreground-900">{topic.title}</span>
                      <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                        <i className={`${trendIcon[topic.trend]} ${trendClass[topic.trend]}`} />
                        {formatHeat(topic.heat)} 热度
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-bookmark-3-line text-secondary-600" />
              编辑推荐
            </h3>
            <div className="mt-3 divide-y divide-background-200">
              {recommended.map((item) => (
                <Link key={item.slug} to={`/article/${item.slug}`} className="group block py-3">
                  <span className="block text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-500">
                    {item.categoryName} · {item.relativeTime}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}